import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Container, NbCard, Chip, NbButton } from "@/components/nb";
import ProblemBoard from "@/components/ProblemBoard";
import {
  ArrowLeft, AlertTriangle, Trophy, Newspaper, MapPin, ExternalLink,
  Thermometer, Wind, Droplets, User, Lightbulb, Calendar, Sparkles,
  Camera, ChevronLeft, ChevronRight, X,
} from "lucide-react";

// SVG gradient hero per district — always looks great and avoids broken image URLs
const REGION_HERO_GRADIENT = {
  "Khasi Hills":   ["#FFAEBC", "#FBE7C6", "#B4F8C8"],
  "Garo Hills":    ["#B4F8C8", "#A0E7E5", "#FBE7C6"],
  "Jaintia Hills": ["#A0E7E5", "#B4F8C8", "#FFAEBC"],
};

function HeroBanner({ district }) {
  const stops = REGION_HERO_GRADIENT[district.region] || ["#B4F8C8", "#FFAEBC", "#FBE7C6"];
  return (
    <div className="relative h-56 md:h-80 w-full overflow-hidden">
      <svg viewBox="0 0 1600 500" preserveAspectRatio="xMidYMid slice" className="w-full h-full">
        <defs>
          <linearGradient id={`hg-${district.id}`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={stops[0]} />
            <stop offset="55%" stopColor={stops[1]} />
            <stop offset="100%" stopColor={stops[2]} />
          </linearGradient>
          <pattern id={`hp-${district.id}`} width="24" height="24" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="1.4" fill="#0A0A0A" opacity="0.08" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="1600" height="500" fill={`url(#hg-${district.id})`} />
        <rect x="0" y="0" width="1600" height="500" fill={`url(#hp-${district.id})`} />
        {/* Stylised mountains */}
        <path d="M0,420 L180,260 L320,340 L520,220 L720,340 L920,240 L1160,320 L1360,260 L1600,340 L1600,500 L0,500 Z" fill="rgba(10,10,10,0.85)" />
        <path d="M0,470 L200,380 L400,440 L620,360 L820,430 L1040,380 L1240,430 L1440,380 L1600,440 L1600,500 L0,500 Z" fill="#0A0A0A" />
        {/* Sun */}
        <circle cx="1200" cy="140" r="70" fill="#FBE7C6" stroke="#0A0A0A" strokeWidth="4" />
      </svg>
      <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.55), rgba(0,0,0,0.05) 55%)" }} />
    </div>
  );
}

const REGION_CLS = {
  "Garo Hills": "bg-sl-primary",
  "Khasi Hills": "bg-sl-secondary",
  "Jaintia Hills": "bg-sl-accent",
};

function formatDate(s) {
  if (!s) return "";
  try {
    const d = new Date(s);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

export default function MeghalayaDistrict() {
  const { districtId } = useParams();
  const [district, setDistrict] = useState(null);
  const [weather, setWeather] = useState(null);
  const [news, setNews] = useState(null);
  const [mentor, setMentor] = useState(null);
  const [loadingNews, setLoadingNews] = useState(true);
  const [photos, setPhotos] = useState(null);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [error, setError] = useState(null);
  const [yearOpen, setYearOpen] = useState(null);
  const stripRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setDistrict(null); setWeather(null); setNews(null); setMentor(null);
    setPhotos(null); setLoadingNews(true); setLoadingPhotos(true); setError(null);
    (async () => {
      try {
        const [d, w, m] = await Promise.all([
          api.get(`/meghalaya/districts/${districtId}`),
          api.get(`/meghalaya/weather?district_id=${districtId}`).catch(() => null),
          api.get(`/meghalaya/mentor?district_id=${districtId}`).catch(() => null),
        ]);
        if (cancelled) return;
        setDistrict(d.data);
        setWeather(w?.data || null);
        setMentor(m?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "District not found");
      }
      // photos + news in parallel (both are slower)
      api.get(`/meghalaya/photos?district_id=${districtId}&limit=10`)
        .then((r) => !cancelled && setPhotos(r.data))
        .catch(() => !cancelled && setPhotos({ items: [] }))
        .finally(() => !cancelled && setLoadingPhotos(false));
      try {
        const n = await api.get(`/meghalaya/news?district_id=${districtId}&limit=8`);
        if (!cancelled) setNews(n.data);
      } catch {
        if (!cancelled) setNews({ items: [] });
      } finally {
        if (!cancelled) setLoadingNews(false);
      }
    })();
    return () => { cancelled = true; };
  }, [districtId]);

  const scrollStrip = (dir) => {
    const el = stripRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 340, behavior: "smooth" });
  };

  if (error) {
    return (
      <Container>
        <NbCard className="!p-8">
          <h1 className="font-heading font-black text-3xl">District not found</h1>
          <p className="mt-2 text-neutral-700">The district id "{districtId}" doesn't match any Meghalaya district.</p>
          <Link to="/meghalaya" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-heading font-bold text-sm uppercase">
            <ArrowLeft className="w-4 h-4" /> Back to map
          </Link>
        </NbCard>
      </Container>
    );
  }

  if (!district) {
    return (
      <Container>
        <div className="font-mono-sl text-xs uppercase text-neutral-500">Loading district…</div>
      </Container>
    );
  }

  return (
    <Container>
      <Link to="/meghalaya" data-testid="back-to-map" className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full nb-border font-mono-sl text-[10px] uppercase bg-white nb-hover">
        <ArrowLeft className="w-3 h-3" /> Back to map
      </Link>

      {/* HERO */}
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative rounded-2xl nb-border nb-shadow-xl overflow-hidden bg-white"
        data-testid="district-hero"
      >
        <HeroBanner district={district} />
        <div className="absolute inset-x-0 bottom-0 p-5 md:p-7 text-white">
          <span className="inline-block font-mono-sl text-[10px] uppercase tracking-widest bg-white text-black nb-border rounded-full px-2 py-0.5">{district.region.toUpperCase()}</span>
          <h1 className="mt-2 font-heading font-black text-4xl md:text-6xl tracking-tighter leading-[0.95] drop-shadow-[2px_2px_0_rgba(0,0,0,0.7)]">{district.name}</h1>
          <div className="mt-1 flex items-center gap-4 flex-wrap font-mono-sl text-[11px] uppercase">
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> Capital · {district.capital}</span>
            <span>{district.lat?.toFixed(3)}°N, {district.lon?.toFixed(3)}°E</span>
          </div>
        </div>
      </motion.div>

      {/* WEATHER + MENTOR */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-5">
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
          className="md:col-span-2"
          data-testid="weather-panel"
        >
          <NbCard color="accent" className="!p-5 h-full">
            <div className="flex items-center gap-2 mb-2 font-mono-sl text-[10px] uppercase">
              <Sparkles className="w-4 h-4" /> Live weather · Open-Meteo
            </div>
            {weather ? (
              <>
                <div className="flex items-center gap-5 flex-wrap">
                  <div className="text-6xl">{weather.emoji}</div>
                  <div>
                    <div className="font-heading font-black text-5xl tracking-tighter">{Math.round(weather.temp_c)}°C</div>
                    <div className="font-mono-sl text-xs uppercase text-neutral-700">{weather.label}</div>
                  </div>
                  <div className="flex items-center gap-4 ml-auto">
                    <div className="flex items-center gap-1"><Droplets className="w-4 h-4" /> {weather.humidity}%</div>
                    <div className="flex items-center gap-1"><Wind className="w-4 h-4" /> {weather.wind_kmh} km/h</div>
                  </div>
                </div>

                {weather.forecast?.length > 0 && (
                  <>
                    <details className="mt-3 group" data-testid="forecast-details" open>
                      <summary className="cursor-pointer font-mono-sl text-[10px] uppercase text-neutral-700 flex items-center gap-1 select-none">
                        <span className="group-open:hidden">Show</span><span className="hidden group-open:inline">Hide</span> 5-day forecast
                      </summary>
                      <div className="mt-2 grid grid-cols-5 gap-2" data-testid="forecast-strip">
                        {weather.forecast.map((f, i) => {
                          const d = new Date(f.date);
                          const day = i === 0 ? "Today" : d.toLocaleDateString("en-IN", { weekday: "short" });
                          return (
                            <motion.div
                              key={f.date}
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.05 * i }}
                              data-testid={`forecast-day-${i}`}
                              className="rounded-lg nb-border bg-white p-2 text-center"
                            >
                              <div className="font-mono-sl text-[10px] uppercase text-neutral-600">{day}</div>
                              <div className="text-2xl leading-tight">{f.emoji}</div>
                              <div className="font-heading font-black text-sm leading-tight">
                                {f.temp_max != null ? Math.round(f.temp_max) : "–"}°
                                <span className="font-mono-sl text-[10px] text-neutral-500 ml-1">
                                  {f.temp_min != null ? Math.round(f.temp_min) : "–"}°
                                </span>
                              </div>
                              {f.rain_prob != null && (
                                <div className="font-mono-sl text-[9px] uppercase text-neutral-600 flex items-center justify-center gap-0.5 mt-0.5">
                                  <Droplets className="w-2.5 h-2.5" /> {f.rain_prob}%
                                </div>
                              )}
                            </motion.div>
                          );
                        })}
                      </div>
                    </details>
                  </>
                )}
              </>
            ) : (
              <div className="text-neutral-600 text-sm">Weather is offline right now — try refreshing.</div>
            )}
          </NbCard>
        </motion.div>

        {mentor && (
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
            data-testid="mentor-card"
          >
            <NbCard color={mentor.bg || "primary"} className="!p-5 h-full">
              <div className="font-mono-sl text-[10px] uppercase mb-2 flex items-center gap-1"><User className="w-3 h-3" /> Regional mentor</div>
              <div className="text-4xl">{mentor.avatar}</div>
              <div className="mt-2 font-heading font-black text-xl tracking-tight">{mentor.name}</div>
              <div className="text-sm text-neutral-800">{mentor.role}</div>
              <div className="mt-2 text-sm">Focus: {mentor.focus}</div>
              <Link
                to="/career/mentors"
                data-testid="mentor-cta"
                className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full nb-border font-mono-sl text-[10px] uppercase bg-white nb-hover"
              >
                Find more mentors <ExternalLink className="w-3 h-3" />
              </Link>
            </NbCard>
          </motion.div>
        )}
      </div>

      {/* PROBLEMS + ACHIEVEMENTS */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
        <NbCard className="!p-5" data-testid="district-problems">
          <div className="flex items-center gap-2 mb-2"><AlertTriangle className="w-5 h-5 text-red-600" /><h3 className="font-heading font-black text-lg tracking-tight">Problems on the ground</h3></div>
          <ul className="space-y-1.5">
            {district.problems.map((p, i) => <li key={i} className="flex gap-2"><span className="text-red-600">•</span><span>{p}</span></li>)}
          </ul>
        </NbCard>
        <NbCard color="secondary" className="!p-5" data-testid="district-achievements">
          <div className="flex items-center gap-2 mb-2"><Trophy className="w-5 h-5" /><h3 className="font-heading font-black text-lg tracking-tight">Achievements & strengths</h3></div>
          <ul className="space-y-1.5">
            {district.achievements.map((p, i) => <li key={i} className="flex gap-2"><span>★</span><span>{p}</span></li>)}
          </ul>
        </NbCard>
      </div>

      {/* PHOTO STRIP (Wikimedia Commons) */}
      <div className="mt-6" data-testid="photo-section">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Camera className="w-5 h-5" />
            <h3 className="font-heading font-black text-2xl tracking-tight">Landmarks · {district.name}</h3>
            <span className="font-mono-sl text-[10px] uppercase text-neutral-500">via Wikimedia Commons</span>
          </div>
          <div className="flex gap-1">
            <button onClick={() => scrollStrip(-1)} data-testid="photo-prev" className="p-2 rounded-lg nb-border bg-white nb-hover" aria-label="Previous"><ChevronLeft className="w-4 h-4" /></button>
            <button onClick={() => scrollStrip(1)} data-testid="photo-next" className="p-2 rounded-lg nb-border bg-white nb-hover" aria-label="Next"><ChevronRight className="w-4 h-4" /></button>
          </div>
        </div>

        {loadingPhotos && <div className="font-mono-sl text-[10px] uppercase text-neutral-500">Loading landmark photos…</div>}
        {!loadingPhotos && photos?.items?.length === 0 && (
          <NbCard className="!p-4"><div className="text-neutral-700 text-sm">No public-domain photos found for this district yet.</div></NbCard>
        )}
        {photos?.items?.length > 0 && (
          <div
            ref={stripRef}
            data-testid="photo-strip"
            className="flex gap-3 overflow-x-auto snap-x snap-mandatory pb-2"
            style={{ scrollbarWidth: "thin" }}
          >
            {photos.items.map((p, i) => (
              <motion.a
                key={p.thumb}
                href={p.page}
                target="_blank"
                rel="noreferrer"
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ delay: 0.04 * i }}
                data-testid={`photo-${i}`}
                className="snap-start shrink-0 w-[300px] md:w-[340px] rounded-xl overflow-hidden nb-border nb-shadow nb-hover-lg bg-white"
              >
                <div
                  className="h-48 w-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${p.thumb})` }}
                  aria-label={p.title}
                />
                <div className="p-3">
                  <div className="font-heading font-bold text-sm tracking-tight line-clamp-2">{p.title}</div>
                  {p.caption && <div className="mt-1 text-xs text-neutral-700 line-clamp-2">{p.caption}</div>}
                  <div className="mt-1 font-mono-sl text-[10px] uppercase text-neutral-500 truncate">© {p.author} · {p.license}</div>
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </div>

      {/* NEWS */}
      <div className="mt-6" data-testid="news-section">
        <div className="flex items-center gap-2 mb-3"><Newspaper className="w-5 h-5" /><h3 className="font-heading font-black text-2xl tracking-tight">Latest news · {district.name}</h3></div>
        {loadingNews && <div className="font-mono-sl text-[10px] uppercase text-neutral-500">Loading live news…</div>}
        {!loadingNews && news?.items?.length === 0 && (
          <NbCard className="!p-4">
            <div className="text-neutral-700 text-sm">No news found right now. Try again later.</div>
          </NbCard>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {news?.items?.map((n, i) => (
            <motion.a
              key={i}
              href={n.link}
              target="_blank"
              rel="noreferrer"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.03 * i }}
              data-testid={`news-item-${i}`}
              className="block"
            >
              <NbCard className="!p-4 h-full nb-hover-lg">
                <div className="flex items-center gap-2 mb-1 font-mono-sl text-[10px] uppercase text-neutral-600">
                  <span>{n.source || "News"}</span>
                  {n.pub_date && <span className="text-neutral-400">· {formatDate(n.pub_date)}</span>}
                </div>
                <div className="font-heading font-black text-lg tracking-tight leading-snug">{n.title}</div>
                {n.description && <div className="mt-1 text-sm text-neutral-700 line-clamp-2">{n.description}</div>}
                <div className="mt-2 inline-flex items-center gap-1 font-mono-sl text-[10px] uppercase underline">
                  Read <ExternalLink className="w-3 h-3" />
                </div>
              </NbCard>
            </motion.a>
          ))}
        </div>
      </div>

      {/* TIMELINE */}
      <div className="mt-8" data-testid="district-timeline">
        <div className="flex items-center gap-2 mb-3"><Calendar className="w-5 h-5" /><h3 className="font-heading font-black text-2xl tracking-tight">Govt actions · 2022 → 2026</h3></div>
        <div className="text-sm text-neutral-700 mb-3">Click any year to see the official government initiatives taken in {district.name}.</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[2022, 2023, 2024, 2025, 2026].map((yr) => {
            const items = district.initiatives.filter((i) => i.year === yr);
            return (
              <button
                key={yr}
                onClick={() => items.length && setYearOpen(yr)}
                disabled={!items.length}
                data-testid={`d-year-${yr}`}
                className={`text-left rounded-xl nb-border p-4 min-h-[140px] flex flex-col ${items.length ? "bg-sl-accent nb-shadow nb-hover-lg cursor-pointer" : "bg-white cursor-default opacity-70"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-heading font-black text-2xl tracking-tight">{yr}</div>
                  {items.length > 0 && <span className="font-mono-sl text-[10px] uppercase bg-white nb-border rounded-full px-2 py-0.5">{items.length} act{items.length === 1 ? "" : "s"}</span>}
                </div>
                {items.length === 0 ? (
                  <div className="mt-2 text-neutral-500 text-sm italic">— no logged action —</div>
                ) : (
                  <ul className="mt-2 space-y-1">
                    {items.slice(0, 2).map((it, i) => (
                      <li key={i} className="flex gap-2 items-start text-sm"><Lightbulb className="w-3 h-3 mt-1 shrink-0" /><span className="line-clamp-2">{it.title}</span></li>
                    ))}
                    {items.length > 2 && <li className="font-mono-sl text-[10px] uppercase text-neutral-700">+{items.length - 2} more · click to open</li>}
                  </ul>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* YEAR DETAIL MODAL */}
      <AnimatePresence>
        {yearOpen && (() => {
          const items = district.initiatives.filter((i) => i.year === yearOpen);
          return (
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/50 grid place-items-center p-5"
              onClick={() => setYearOpen(null)}
              data-testid="year-modal"
            >
              <motion.div
                initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }}
                transition={{ type: "spring", stiffness: 220, damping: 20 }}
                onClick={(e) => e.stopPropagation()}
                className="max-w-2xl w-full bg-white nb-border nb-shadow-xl rounded-2xl overflow-hidden"
              >
                <div className="bg-sl-accent p-5 border-b-2 border-black flex items-start gap-3">
                  <Calendar className="w-6 h-6 mt-1" />
                  <div className="flex-1">
                    <div className="font-mono-sl text-[10px] uppercase">OFFICIAL GOVT INITIATIVES · {district.name}</div>
                    <div className="font-heading font-black text-4xl tracking-tighter">{yearOpen}</div>
                  </div>
                  <button onClick={() => setYearOpen(null)} data-testid="year-modal-close" className="p-2 rounded-lg bg-white nb-border nb-hover"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 max-h-[60vh] overflow-y-auto">
                  {items.length === 0 && <div className="text-neutral-600 italic">No official initiatives logged for this year.</div>}
                  <ol className="space-y-3">
                    {items.map((it, i) => (
                      <li key={i} className="flex gap-3 rounded-lg nb-border p-3 bg-white" data-testid={`year-initiative-${i}`}>
                        <div className="shrink-0 w-8 h-8 rounded-full bg-sl-primary nb-border grid place-items-center font-heading font-black">{i + 1}</div>
                        <div>
                          <div className="font-heading font-bold text-base tracking-tight">{it.title}</div>
                          <div className="mt-1 flex flex-wrap gap-2 items-center">
                            <span className="font-mono-sl text-[10px] uppercase bg-sl-tertiary nb-border rounded-full px-2 py-0.5">by · {it.by}</span>
                            <span className="font-mono-sl text-[10px] uppercase text-neutral-500">{it.year}</span>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ol>
                  <div className="mt-4 rounded-lg bg-sl-tertiary nb-border p-3 font-mono-sl text-[10px] uppercase">
                    Source · Meghalaya Govt public reports & press releases · verify via meghalaya.gov.in
                  </div>
                </div>
              </motion.div>
            </motion.div>
          );
        })()}
      </AnimatePresence>

      {/* COMMUNITY BOARD */}
      <ProblemBoard districtId={district.id} districtName={district.name} />

      {/* CTA */}
      <div className="mt-8 flex flex-wrap gap-3">
        <Link to="/meghalaya" data-testid="cta-map" className="px-5 py-3 rounded-lg nb-border nb-shadow nb-hover bg-white font-heading font-bold uppercase text-sm inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> Back to full map
        </Link>
        <Link to="/ideas/submit" data-testid="cta-idea" className="px-5 py-3 rounded-lg nb-border nb-shadow nb-hover bg-black text-white font-heading font-bold uppercase text-sm inline-flex items-center gap-2">
          <Lightbulb className="w-4 h-4" /> Share an idea for {district.capital}
        </Link>
      </div>
    </Container>
  );
}
